"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Droplets, ScanLine, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  id: "01",
  title: "Pressure Clean",
  description: "Thorough removal of dirt, mold, and contaminants to ensure a pristine base for adhesion.",
  icon: Droplets
}, {
  id: "02",
  title: "Precision Filling & Sanding",
  description: "Meticulous smoothing of imperfections to create a flawless, uniform surface texture.",
  icon: ScanLine
}, {
  id: "03",
  title: "Prime & Seal",
  description: "Application of premium sealants to lock in the surface and enhance paint durability.",
  icon: Shield
}];
export default function ThePrepWorkProtocol() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const [progressValues, setProgressValues] = useState([0, 0, 0]);
  useEffect(() => {
    if (isVisible) {
      const timeouts = steps.map((_, index) => setTimeout(() => {
        setProgressValues(prev => {
          const newValues = [...prev];
          newValues[index] = 100;
          return newValues;
        });
      }, index * 200 + 100));
      return () => timeouts.forEach(t => clearTimeout(t));
    }
  }, [isVisible]);
  return <section id="the-prep-work-protocol" ref={ref} className="relative w-full py-20 lg:py-28 bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] bg-[size:40px_40px]" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div className="relative h-full min-h-[400px] lg:min-h-[600px] w-full rounded-2xl overflow-hidden shadow-2xl order-2 lg:order-1 group">
            <div className="absolute inset-0 bg-muted/20 z-10 mix-blend-multiply" />
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559587/img/macro_sanded_surface_detail.png" alt="Macro photography of a perfectly sanded surface showing wood grain texture" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            
            {}
            <div className="absolute bottom-8 left-8 z-20 bg-background/90 backdrop-blur-md border border-border/50 px-6 py-4 rounded-lg shadow-lg max-w-[280px]">
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">
                Surface Analysis
              </p>
              <p className="text-sm font-medium text-foreground">
                Micro-abrasion ready for premium coating application.
              </p>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-8 order-1 lg:order-2">
            <div className="space-y-4 mb-4">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                The Prep-Work Protocol
              </h2>
              <p className="text-lg text-muted-foreground max-w-md">
                At ANV Painting, we believe 90% of a perfect finish happens before the paint can is even opened.
              </p>
            </div>

            <div className="flex flex-col gap-6">
              {steps.map((step, i) => <Card key={step.id} data-index={i} className="border-border/60 bg-card/50 backdrop-blur-sm hover:bg-card transition-colors duration-300 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-5">
                      {}
                      <div className="shrink-0 flex items-center justify-center size-12 rounded-lg bg-primary/10 text-primary">
                        <step.icon className="size-6" strokeWidth={1.5} />
                      </div>

                      <div className="flex-1 space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-semibold text-foreground">
                            {step.title}
                          </h3>
                          <span className="font-mono text-2xl font-bold text-muted-foreground/40">
                            {step.id}
                          </span>
                        </div>
                        
                        <p className="text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>

                        <div className="pt-2">
                          <div className="flex justify-between text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                            <span>Progress</span>
                            <span>{progressValues[i]}%</span>
                          </div>
                          <Progress value={progressValues[i]} className="h-1.5" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}